import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface ServerPaginationProps {
  /** 1-based page index. */
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  /** Required unless `hidePageSize` is set. */
  onPageSizeChange?: (size: number) => void
  pageSizeOptions?: number[]
  itemsPerPageLabel?: string
  /**
   * Drop the page-size picker for a list whose page size is fixed. A select
   * with one option and a handler that does nothing is a control that lies
   * about being a control.
   */
  hidePageSize?: boolean
  /** "center" puts the page buttons in the middle; only meaningful with `hidePageSize`. */
  align?: "between" | "center"
  className?: string
}

/**
 * Server-side pagination footer. Pairs a page-size picker with numbered
 * page buttons that collapse with ellipses for large page counts.
 *
 * Use under a `<DataTable />` driven by offset+limit fetches: the parent
 * owns `page`/`pageSize` state, recomputes `offset = (page - 1) * pageSize`,
 * and re-fetches whenever either value changes.
 */
export function ServerPagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  itemsPerPageLabel = "Items per page",
  hidePageSize = false,
  align = "between",
  className,
}: ServerPaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const current = Math.min(Math.max(1, page), totalPages)
  const buttons = paginationButtons(current, totalPages)

  // Hide entirely when there's nothing to paginate AND the user is on the
  // smallest page-size option — nothing useful to show.
  if (total === 0) return null

  const centered = align === "center" && hidePageSize

  return (
    <div
      className={cn(
        "flex flex-col md:flex-row items-center gap-3 px-3 md:px-4 py-3",
        centered ? "justify-center" : "justify-between border-t border-neutral-200 max-md:items-start",
        className
      )}
    >
      {centered ? null : hidePageSize ? (
        <div />
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-sm text-neutral-600">{itemsPerPageLabel}</span>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange?.(Number(v))}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((sz) => (
                <SelectItem key={sz} value={String(sz)}>
                  {sz}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          disabled={current <= 1}
          onClick={() => onPageChange(current - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {buttons.map((b, i) =>
          b === "ellipsis" ? (
            <span
              key={`gap-${i}`}
              className="px-1 text-sm text-neutral-700 select-none"
            >
              …
            </span>
          ) : (
            <Button
              key={b}
              variant={b === current ? "default" : "ghost"}
              size="icon"
              className="h-8 w-8"
              onClick={() => onPageChange(b)}
            >
              {b}
            </Button>
          )
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          disabled={current >= totalPages}
          onClick={() => onPageChange(current + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

/**
 * Returns a compact page-button list. ≤ 7 pages → all shown. Larger sets get
 * collapsed with "ellipsis" placeholders, e.g. `[1, ..., 4, 5, 6, ..., 12]`.
 */
function paginationButtons(
  current: number,
  total: number
): (number | "ellipsis")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  const result: (number | "ellipsis")[] = [1]
  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)
  if (start > 2) result.push("ellipsis")
  for (let i = start; i <= end; i++) result.push(i)
  if (end < total - 1) result.push("ellipsis")
  result.push(total)
  return result
}
